import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { RefreshCw, AlertTriangle, BarChart3, Settings as SettingsIcon, Plus, X, Check, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const ALL_METRICS = [
  { key: "daily_budget", label: "Daily budget", fmt: (v) => v == null ? "—" : `₹${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
  { key: "spend", label: "Spend", fmt: (v) => `₹${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}` },
  { key: "impressions", label: "Impr.", fmt: (v) => Number(v || 0).toLocaleString() },
  { key: "reach", label: "Reach", fmt: (v) => Number(v || 0).toLocaleString() },
  { key: "frequency", label: "Frequency", fmt: (v) => Number(v || 0).toFixed(2) },
  { key: "clicks", label: "Clicks", fmt: (v) => Number(v || 0).toLocaleString() },
  { key: "ctr", label: "CTR", fmt: (v) => `${Number(v || 0).toFixed(2)}%` },
  { key: "cpm", label: "CPM", fmt: (v) => `₹${Number(v || 0).toFixed(2)}` },
  { key: "cpc", label: "CPC", fmt: (v) => `₹${Number(v || 0).toFixed(2)}` },
  { key: "conversions", label: "Conv.", fmt: (v) => Number(v || 0).toLocaleString() },
  { key: "revenue", label: "Revenue", fmt: (v) => `₹${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}` },
  { key: "purchase_roas", label: "ROAS", fmt: (v) => `${Number(v || 0).toFixed(2)}x` },
];

const DATE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_7d", label: "Last 7 days" },
  { id: "last_30d", label: "Last 30 days" },
  { id: "last_90d", label: "Last 90 days" },
  { id: "maximum", label: "Max" },
  { id: "custom", label: "Custom…" },
];

// --- Per-user, per-block view preferences stored in localStorage ---
function prefsKey(userId, blockId) { return `metaads:${userId || "anon"}:${blockId}`; }
function loadPrefs(userId, blockId) {
  try { return JSON.parse(localStorage.getItem(prefsKey(userId, blockId)) || "{}") || {}; }
  catch { return {}; }
}
function savePrefs(userId, blockId, prefs) {
  try { localStorage.setItem(prefsKey(userId, blockId), JSON.stringify(prefs)); } catch { /* quota */ }
}

export default function MetaAdsBlock({ block, isTeam, onUpdateBlock }) {
  const { id: clientId } = useParams();
  const { user } = useAuth();

  const cfg = block.config || {};
  const selectedCampaignIds = useMemo(() => cfg.selected_campaign_ids || [], [cfg.selected_campaign_ids]);
  const visibleMetrics = cfg.visible_metrics && cfg.visible_metrics.length ? cfg.visible_metrics : ["daily_budget", "spend", "impressions", "clicks", "ctr", "cpm", "cpc"];
  const rowLimit = cfg.row_limit || 25;
  const extraColumns = cfg.extra_columns || [];
  const customData = cfg.custom_data || {};
  const colWidths = cfg.col_widths || {};

  // Per-user view prefs (localStorage).
  const initialPrefs = useMemo(() => {
    const p = loadPrefs(user?.id, block.id);
    return {
      date_preset: p.date_preset || cfg.date_preset || "last_30d",
      since: p.since || "",
      until: p.until || "",
      view_mode: p.view_mode || cfg.view_mode || "summary",     // summary | daywise
      level: p.level || "campaign",                              // campaign | adset | ad
      expanded: p.expanded || {},                                // { [campaign_id]: true, [`cs:adsetId`]: true }
    };
  }, [user?.id, block.id, cfg.date_preset, cfg.view_mode]);

  const [datePreset, setDatePreset] = useState(initialPrefs.date_preset);
  const [since, setSince] = useState(initialPrefs.since);
  const [until, setUntil] = useState(initialPrefs.until);
  const [viewMode, setViewMode] = useState(initialPrefs.view_mode);
  const [level, setLevel] = useState(initialPrefs.level);
  const [expanded, setExpanded] = useState(initialPrefs.expanded);

  // Persist whenever any pref changes
  useEffect(() => {
    savePrefs(user?.id, block.id, { date_preset: datePreset, since, until, view_mode: viewMode, level, expanded });
  }, [user?.id, block.id, datePreset, since, until, viewMode, level, expanded]);

  const [data, setData] = useState(null);          // campaign summary (always loaded for picker)
  const [daily, setDaily] = useState(null);         // daywise response
  const [levelData, setLevelData] = useState(null); // adset/ad response when level!=campaign
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [childCache, setChildCache] = useState({}); // {`cs:campId`: {adsets, loading}, `ads:adsetId`: {ads, loading}}

  // Build the range query string once per load
  const rangeQS = useMemo(() => {
    if (datePreset === "custom" && since && until) return `since=${since}&until=${until}`;
    return `date_preset=${datePreset}`;
  }, [datePreset, since, until]);

  // Column resize (shared across all tables)
  const [resizingCol, setResizingCol] = useState(null);
  const startResize = (e, colKey) => {
    e.preventDefault(); e.stopPropagation();
    setResizingCol(colKey);
    const startX = e.clientX;
    const startW = colWidths[colKey] || e.currentTarget.parentElement?.getBoundingClientRect().width || 120;
    const onMove = (mv) => {
      const next = Math.max(60, Math.round(startW + (mv.clientX - startX)));
      document.querySelectorAll(`[data-col-key="${colKey}"]`).forEach((el) => { el.style.width = `${next}px`; el.style.minWidth = `${next}px`; });
    };
    const onUp = (up) => {
      const final = Math.max(60, Math.round(startW + (up.clientX - startX)));
      setResizingCol(null);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      onUpdateBlock(block.id, { config: { ...cfg, col_widths: { ...colWidths, [colKey]: final } } });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const colStyle = (key) => colWidths[key] ? { width: `${colWidths[key]}px`, minWidth: `${colWidths[key]}px` } : undefined;

  // Guard: don't fetch with incomplete custom range
  const rangeReady = datePreset !== "custom" || (since && until);

  const load = useCallback(async () => {
    if (!rangeReady) { setLoading(false); return; }
    setLoading(true); setErr("");
    try {
      // Always refresh the campaign list (needed for picker + for drilling down)
      const camps = api.get(`/clients/${clientId}/meta_ads/campaigns?${rangeQS}`);
      if (viewMode === "daywise") {
        const [cRes, dRes] = await Promise.all([
          camps,
          api.get(`/clients/${clientId}/meta_ads/daily_spend?${rangeQS}`),
        ]);
        setData(cRes.data); setDaily(dRes.data);
      } else if (level === "adset") {
        const [cRes, aRes] = await Promise.all([
          camps,
          api.get(`/clients/${clientId}/meta_ads/adsets?${rangeQS}`),
        ]);
        setData(cRes.data); setLevelData(aRes.data);
      } else if (level === "ad") {
        const [cRes, aRes] = await Promise.all([
          camps,
          api.get(`/clients/${clientId}/meta_ads/ads?${rangeQS}`),
        ]);
        setData(cRes.data); setLevelData(aRes.data);
      } else {
        const cRes = await camps;
        setData(cRes.data); setLevelData(null);
      }
      setChildCache({}); // range changed → nuke inline expansion cache
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Meta Ads data");
    } finally { setLoading(false); }
  }, [clientId, rangeQS, viewMode, level, rangeReady]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.campaigns) return [];
    let list = data.campaigns;
    if (selectedCampaignIds.length) list = list.filter((c) => selectedCampaignIds.includes(c.campaign_id));
    return list.slice(0, rowLimit);
  }, [data, selectedCampaignIds, rowLimit]);

  const renderedMetricCols = ALL_METRICS.filter((m) => visibleMetrics.includes(m.key));
  const campaignNameById = useMemo(() => {
    const m = {};
    (data?.campaigns || []).forEach((c) => { m[c.campaign_id] = c.name; });
    return m;
  }, [data]);

  const toggleRow = useCallback(async (key, loader) => {
    const open = !expanded[key];
    setExpanded((prev) => ({ ...prev, [key]: open }));
    if (open && loader && !childCache[key]) {
      setChildCache((prev) => ({ ...prev, [key]: { loading: true } }));
      try {
        const payload = await loader();
        setChildCache((prev) => ({ ...prev, [key]: { loading: false, ...payload } }));
      } catch (e) {
        setChildCache((prev) => ({ ...prev, [key]: { loading: false, error: e?.response?.data?.detail || "Failed" } }));
      }
    }
  }, [expanded, childCache]);

  const loadAdsetsForCampaign = useCallback((campId) => async () => {
    const r = await api.get(`/clients/${clientId}/meta_ads/adsets?${rangeQS}&campaign_id=${campId}`);
    return { adsets: r.data.adsets || [] };
  }, [clientId, rangeQS]);

  const loadAdsForAdset = useCallback((adsetId) => async () => {
    const r = await api.get(`/clients/${clientId}/meta_ads/ads?${rangeQS}&adset_id=${adsetId}`);
    return { ads: r.data.ads || [] };
  }, [clientId, rangeQS]);

  const rangeLabel = datePreset === "custom" && since && until ? `${since} → ${until}` : (DATE_PRESETS.find((p) => p.id === datePreset)?.label || datePreset);

  return (
    <div className="tt-card p-5 anim-fade-up" data-testid="meta-ads-block">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5 text-zinc-300" />
          <span className="t-eyebrow">Meta Ads · {cfg.title || "Active campaigns"}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Level toggle (per-user) — hidden in daywise view */}
          {viewMode !== "daywise" && (
            <div className="inline-flex rounded-md border border-zinc-800 overflow-hidden" data-testid="meta-ads-level-toggle">
              {[
                { id: "campaign", label: "Campaigns" },
                { id: "adset", label: "Ad sets" },
                { id: "ad", label: "Ads" },
              ].map((l) => (
                <button key={l.id} onClick={() => setLevel(l.id)}
                  data-testid={`meta-ads-level-${l.id}`}
                  className={`px-2.5 h-8 text-[11px] ${level === l.id ? "bg-white text-black" : "bg-[#0a0a0a] text-zinc-400 hover:text-zinc-200"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          )}
          <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}
            className="bg-[#0a0a0a] border border-zinc-800 rounded-md h-8 text-[11px] px-2"
            data-testid="meta-ads-view-mode">
            <option value="summary">Summary</option>
            <option value="daywise">Daywise spend</option>
          </select>
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)}
            className="bg-[#0a0a0a] border border-zinc-800 rounded-md h-8 text-[11px] px-2"
            data-testid="meta-ads-date-preset">
            {DATE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          {datePreset === "custom" && (
            <>
              <Input type="date" value={since} onChange={(e) => setSince(e.target.value)}
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[11px] w-[130px]" data-testid="meta-ads-since" />
              <span className="text-zinc-600 text-[11px]">→</span>
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                className="bg-[#0a0a0a] border-zinc-800 h-8 text-[11px] w-[130px]" data-testid="meta-ads-until" />
            </>
          )}
          {isTeam && (
            <Button size="sm" variant="ghost" onClick={() => setShowSettings((v) => !v)} className="h-8 text-[11px]" data-testid="meta-ads-settings-btn"><SettingsIcon className="w-3 h-3" /></Button>
          )}
          <Button size="sm" variant="ghost" onClick={load} className="h-8 text-[11px]" data-testid="meta-ads-refresh"><RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      {showSettings && isTeam && (
        <MetaAdsBlockSettings
          cfg={cfg}
          campaigns={data?.campaigns || []}
          onSave={(patch) => { onUpdateBlock(block.id, { config: { ...cfg, ...patch } }); setShowSettings(false); }}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!rangeReady ? (
        <div className="text-zinc-500 text-[12px]" data-testid="meta-ads-range-prompt">Pick a start and end date for the custom range.</div>
      ) : err ? (
        <div className="flex items-start gap-2 text-[12px] text-[#FCA5A5] flag-red rounded-md p-3" data-testid="meta-ads-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">{err}</div>
            <div className="text-zinc-500 text-[11px] mt-1">Check the access token + ad account in Client settings.</div>
          </div>
        </div>
      ) : loading && !data && !daily && !levelData ? (
        <div className="text-zinc-600 text-[12px]">Loading…</div>
      ) : viewMode === "daywise" ? (
        <DaywiseSpendView daily={daily} loading={loading} selectedCampaignIds={selectedCampaignIds} rowLimit={rowLimit} />
      ) : level === "campaign" ? (
        filtered.length === 0 ? (
          <div className="text-zinc-600 text-[12px]">No campaigns match this configuration in the selected range.</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[12px] min-w-[680px]">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th data-col-key="campaign" style={colStyle("campaign")} className="text-left p-2 relative">
                    Campaign
                    {isTeam && <span onMouseDown={(e) => startResize(e, "campaign")} className={`col-resize-handle ${resizingCol === "campaign" ? "resizing" : ""}`} title="Drag to resize" />}
                  </th>
                  {renderedMetricCols.map((m) => (
                    <th key={m.key} data-col-key={`m-${m.key}`} style={colStyle(`m-${m.key}`)} className="text-right p-2 relative">
                      {m.label}
                      {isTeam && <span onMouseDown={(e) => startResize(e, `m-${m.key}`)} className={`col-resize-handle ${resizingCol === `m-${m.key}` ? "resizing" : ""}`} title="Drag to resize" />}
                    </th>
                  ))}
                  {extraColumns.map((ec) => (
                    <th key={ec.id} data-col-key={`e-${ec.id}`} style={colStyle(`e-${ec.id}`)} className="text-right p-2 relative">
                      {ec.name}
                      {isTeam && <span onMouseDown={(e) => startResize(e, `e-${ec.id}`)} className={`col-resize-handle ${resizingCol === `e-${ec.id}` ? "resizing" : ""}`} title="Drag to resize" />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const isOpen = !!expanded[c.campaign_id];
                  const child = childCache[c.campaign_id];
                  return (
                    <React.Fragment key={c.campaign_id}>
                      <tr className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`meta-campaign-${c.campaign_id}`}>
                        <td data-col-key="campaign" style={colStyle("campaign")} className="p-2">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => toggleRow(c.campaign_id, loadAdsetsForCampaign(c.campaign_id))}
                              data-testid={`meta-campaign-expand-${c.campaign_id}`}
                              className="w-4 h-4 text-zinc-500 hover:text-white shrink-0">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                            <div className="min-w-0">
                              <div className="text-zinc-100 truncate">{c.name}</div>
                              <div className="text-[10px] text-zinc-600 font-mono">{c.objective}</div>
                            </div>
                          </div>
                        </td>
                        {renderedMetricCols.map((m) => (
                          <td key={m.key} data-col-key={`m-${m.key}`} style={colStyle(`m-${m.key}`)} className="p-2 text-right font-mono text-zinc-200">{m.fmt(c.metrics?.[m.key])}</td>
                        ))}
                        {extraColumns.map((ec) => (
                          <td key={ec.id} data-col-key={`e-${ec.id}`} style={colStyle(`e-${ec.id}`)} className="p-2 text-right">
                            <CustomCell col={ec} value={customData?.[c.campaign_id]?.[ec.id] ?? ""} editable={isTeam}
                              onChange={(val) => {
                                const next = { ...customData };
                                next[c.campaign_id] = { ...(next[c.campaign_id] || {}), [ec.id]: val };
                                onUpdateBlock(block.id, { config: { ...cfg, custom_data: next } });
                              }} />
                          </td>
                        ))}
                      </tr>
                      {isOpen && (
                        <AdsetsInlineRows
                          campaignId={c.campaign_id}
                          child={child}
                          renderedMetricCols={renderedMetricCols}
                          extraColumns={extraColumns}
                          colStyle={colStyle}
                          adsetExpanded={expanded}
                          adsetChildCache={childCache}
                          onToggleAdset={(adsetId) => toggleRow(`as:${adsetId}`, loadAdsForAdset(adsetId))}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-3 text-[10px] text-zinc-700 font-mono uppercase tracking-wider">
              Showing {filtered.length} of {data?.campaigns?.length || 0} active campaigns · {rangeLabel} · ₹ INR · Fetched {data?.fetched_at ? new Date(data.fetched_at).toLocaleTimeString() : "—"}
            </div>
          </div>
        )
      ) : (
        <LevelFlatTable
          level={level}
          data={levelData}
          renderedMetricCols={renderedMetricCols}
          campaignNameById={campaignNameById}
          rowLimit={rowLimit}
          rangeLabel={rangeLabel}
          colStyle={colStyle}
          startResize={startResize}
          resizingCol={resizingCol}
          isTeam={isTeam}
        />
      )}
    </div>
  );
}

// Inline-expanded rows for adsets under a campaign; each adset can further expand to ads.
function AdsetsInlineRows({ campaignId, child, renderedMetricCols, extraColumns, colStyle, adsetExpanded, adsetChildCache, onToggleAdset }) {
  const span = 1 + renderedMetricCols.length + extraColumns.length;
  if (!child || child.loading) {
    return (
      <tr className="bg-[#0b0b0b]"><td colSpan={span} className="pl-8 pr-2 py-2 text-[11px] text-zinc-600">Loading ad sets…</td></tr>
    );
  }
  if (child.error) {
    return <tr className="bg-[#0b0b0b]"><td colSpan={span} className="pl-8 pr-2 py-2 text-[11px] text-[#FCA5A5]">{child.error}</td></tr>;
  }
  const adsets = child.adsets || [];
  if (adsets.length === 0) {
    return <tr className="bg-[#0b0b0b]"><td colSpan={span} className="pl-8 pr-2 py-2 text-[11px] text-zinc-600">No active ad sets.</td></tr>;
  }
  return (
    <>
      {adsets.map((a) => {
        const key = `as:${a.adset_id}`;
        const isOpen = !!adsetExpanded[key];
        const inner = adsetChildCache[key];
        return (
          <React.Fragment key={a.adset_id}>
            <tr className="bg-[#0b0b0b] border-t border-zinc-900" data-testid={`meta-adset-${a.adset_id}`}>
              <td className="p-2 pl-8">
                <div className="flex items-center gap-1.5">
                  <button onClick={() => onToggleAdset(a.adset_id)}
                    data-testid={`meta-adset-expand-${a.adset_id}`}
                    className="w-4 h-4 text-zinc-500 hover:text-white shrink-0">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  <div className="min-w-0">
                    <div className="text-zinc-200 truncate text-[12px]">{a.name}</div>
                    <div className="text-[10px] text-zinc-600 font-mono">{a.optimization_goal}</div>
                  </div>
                </div>
              </td>
              {renderedMetricCols.map((m) => (
                <td key={m.key} data-col-key={`m-${m.key}`} style={colStyle(`m-${m.key}`)} className="p-2 text-right font-mono text-zinc-300">{m.fmt(a.metrics?.[m.key])}</td>
              ))}
              {extraColumns.map((ec) => (<td key={ec.id} className="p-2" />))}
            </tr>
            {isOpen && (
              <AdsInlineRows adsetId={a.adset_id} child={inner} renderedMetricCols={renderedMetricCols} extraColumns={extraColumns} colStyle={colStyle} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

function AdsInlineRows({ adsetId, child, renderedMetricCols, extraColumns, colStyle }) {
  const span = 1 + renderedMetricCols.length + extraColumns.length;
  if (!child || child.loading) {
    return <tr className="bg-[#070707]"><td colSpan={span} className="pl-14 pr-2 py-2 text-[11px] text-zinc-600">Loading ads…</td></tr>;
  }
  if (child.error) {
    return <tr className="bg-[#070707]"><td colSpan={span} className="pl-14 pr-2 py-2 text-[11px] text-[#FCA5A5]">{child.error}</td></tr>;
  }
  const ads = child.ads || [];
  if (ads.length === 0) {
    return <tr className="bg-[#070707]"><td colSpan={span} className="pl-14 pr-2 py-2 text-[11px] text-zinc-600">No active ads.</td></tr>;
  }
  return (
    <>
      {ads.map((ad) => (
        <tr key={ad.ad_id} className="bg-[#070707] border-t border-zinc-900" data-testid={`meta-ad-${ad.ad_id}`}>
          <td className="p-2 pl-14">
            <div className="flex items-center gap-2 min-w-0">
              {ad.thumbnail_url && <img src={ad.thumbnail_url} alt="" className="w-6 h-6 rounded shrink-0 object-cover" />}
              <div className="min-w-0">
                <div className="text-zinc-200 truncate text-[12px]">{ad.name}</div>
                <div className="text-[10px] text-zinc-600 font-mono">{ad.effective_status}</div>
              </div>
            </div>
          </td>
          {renderedMetricCols.map((m) => (
            <td key={m.key} data-col-key={`m-${m.key}`} style={colStyle(`m-${m.key}`)} className="p-2 text-right font-mono text-zinc-300">{m.fmt(ad.metrics?.[m.key])}</td>
          ))}
          {extraColumns.map((ec) => (<td key={ec.id} className="p-2" />))}
        </tr>
      ))}
    </>
  );
}

// Flat ad set / ad view (top-level toggle).
function LevelFlatTable({ level, data, renderedMetricCols, campaignNameById, rowLimit, rangeLabel, colStyle, startResize, resizingCol, isTeam }) {
  const rows = (level === "adset" ? data?.adsets : data?.ads) || [];
  const sliced = rows.slice(0, rowLimit);
  if (!data) return <div className="text-zinc-600 text-[12px]">Loading…</div>;
  if (sliced.length === 0) return <div className="text-zinc-600 text-[12px]">No {level === "adset" ? "ad sets" : "ads"} in this range.</div>;
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-[12px] min-w-[680px]">
        <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
          <tr>
            <th data-col-key="row-name" style={colStyle("row-name")} className="text-left p-2 relative">
              {level === "adset" ? "Ad set" : "Ad"}
              {isTeam && <span onMouseDown={(e) => startResize(e, "row-name")} className={`col-resize-handle ${resizingCol === "row-name" ? "resizing" : ""}`} title="Drag to resize" />}
            </th>
            <th className="text-left p-2">Campaign</th>
            {renderedMetricCols.map((m) => (
              <th key={m.key} data-col-key={`m-${m.key}`} style={colStyle(`m-${m.key}`)} className="text-right p-2 relative">
                {m.label}
                {isTeam && <span onMouseDown={(e) => startResize(e, `m-${m.key}`)} className={`col-resize-handle ${resizingCol === `m-${m.key}` ? "resizing" : ""}`} title="Drag to resize" />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sliced.map((r) => {
            const id = r.adset_id || r.ad_id;
            return (
              <tr key={id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`meta-${level}-${id}`}>
                <td className="p-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.thumbnail_url && <img src={r.thumbnail_url} alt="" className="w-6 h-6 rounded shrink-0 object-cover" />}
                    <div className="min-w-0">
                      <div className="text-zinc-100 truncate">{r.name}</div>
                      <div className="text-[10px] text-zinc-600 font-mono">{r.effective_status}{r.optimization_goal ? ` · ${r.optimization_goal}` : ""}</div>
                    </div>
                  </div>
                </td>
                <td className="p-2 text-zinc-400 truncate max-w-[220px]">{campaignNameById[r.campaign_id] || r.campaign_id}</td>
                {renderedMetricCols.map((m) => (
                  <td key={m.key} data-col-key={`m-${m.key}`} style={colStyle(`m-${m.key}`)} className="p-2 text-right font-mono text-zinc-200">{m.fmt(r.metrics?.[m.key])}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 text-[10px] text-zinc-700 font-mono uppercase tracking-wider">
        Showing {sliced.length} of {rows.length} {level === "adset" ? "ad sets" : "ads"} · {rangeLabel} · ₹ INR · Fetched {data?.fetched_at ? new Date(data.fetched_at).toLocaleTimeString() : "—"}
      </div>
    </div>
  );
}

function DaywiseSpendView({ daily, loading, selectedCampaignIds, rowLimit }) {
  if (!daily) return <div className="text-zinc-600 text-[12px]">Loading day-wise spend…</div>;
  const days = daily.days || [];
  let camps = daily.campaigns || [];
  if (selectedCampaignIds.length) camps = camps.filter((c) => selectedCampaignIds.includes(c.campaign_id));
  camps = camps.slice(0, rowLimit || 25);
  const totals = daily.totals_by_day || [];
  if (!days.length) return <div className="text-zinc-600 text-[12px]">No spend recorded for this range.</div>;
  const grandSpend = totals.reduce((s, d) => s + (d.spend || 0), 0);
  const maxDaily = Math.max(1, ...totals.map((d) => d.spend || 0));
  const shortDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short" }); } catch { return s; } };

  return (
    <div className="space-y-4" data-testid="meta-ads-daywise">
      <div className="rounded-md bg-[#0a0a0a] border border-zinc-800 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="t-eyebrow text-[9px]">Daily spend · totals</div>
          <div className="text-[11px] text-zinc-500 font-mono">Grand total ₹{grandSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="flex items-end gap-1 h-24" data-testid="daywise-totals-chart">
          {totals.map((d) => {
            const pct = (d.spend / maxDaily) * 100;
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group min-w-[6px]" title={`${d.date}\n₹${d.spend.toFixed(2)}`}>
                <span className="text-[8px] text-zinc-500 font-mono opacity-0 group-hover:opacity-100">₹{d.spend.toFixed(0)}</span>
                <div className="w-full rounded-t-sm transition-all" style={{
                  height: `${pct}%`,
                  background: d.spend === 0 ? "rgba(255,255,255,0.06)" : pct > 70 ? "#FAFAFA" : "rgba(255,255,255,0.55)",
                }} />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1.5 text-[8px] text-zinc-700 font-mono">
          <span>{shortDate(days[0])}</span>
          {days.length > 2 && <span>{shortDate(days[Math.floor(days.length / 2)])}</span>}
          <span>{shortDate(days[days.length - 1])}</span>
        </div>
      </div>

      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-[12px] min-w-[680px]" data-testid="daywise-matrix">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0 bg-[#0a0a0a]">
            <tr>
              <th className="text-left p-2 sticky left-0 bg-[#0a0a0a]">Campaign</th>
              {days.map((d) => <th key={d} className="text-right p-2 font-mono">{shortDate(d)}</th>)}
              <th className="text-right p-2 border-l border-zinc-800">Total</th>
            </tr>
          </thead>
          <tbody>
            {camps.length === 0 ? (
              <tr><td colSpan={days.length + 2} className="p-3 text-zinc-600 text-[11px]">No campaigns in this range.</td></tr>
            ) : camps.map((c) => (
              <tr key={c.campaign_id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-2 sticky left-0 bg-[#0c0c0c] truncate max-w-[220px]" title={c.name}>
                  <div className="text-zinc-100 truncate">{c.name}</div>
                </td>
                {c.daily.map((cell) => (
                  <td key={cell.date} className="p-2 text-right font-mono text-zinc-300">
                    {cell.spend > 0 ? `₹${cell.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : <span className="text-zinc-700">·</span>}
                  </td>
                ))}
                <td className="p-2 text-right font-mono text-zinc-100 font-semibold border-l border-zinc-800">
                  ₹{Number(c.total_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-zinc-800 bg-zinc-950">
              <td className="p-2 sticky left-0 bg-zinc-950 text-zinc-200 font-medium">All campaigns</td>
              {totals.map((d) => (
                <td key={d.date} className="p-2 text-right font-mono text-zinc-200">
                  ₹{Number(d.spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
              ))}
              <td className="p-2 text-right font-mono text-white font-semibold border-l border-zinc-800">
                ₹{grandSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-zinc-700 font-mono uppercase tracking-wider">
        {camps.length} campaign{camps.length !== 1 ? "s" : ""} × {days.length} day{days.length !== 1 ? "s" : ""} · INR · Fetched {daily.fetched_at ? new Date(daily.fetched_at).toLocaleTimeString() : "—"}
      </div>
    </div>
  );
}

function CustomCell({ col, value, editable, onChange }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!editable) {
    if (col.type === "currency") return <span className="font-mono">₹{Number(v || 0).toLocaleString()}</span>;
    if (col.type === "number") return <span className="font-mono">{v === "" || v == null ? "—" : Number(v).toLocaleString()}</span>;
    return <span className="text-zinc-300">{v || "—"}</span>;
  }
  return (
    <input
      value={v ?? ""}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== value && onChange(v)}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      className="bg-transparent border border-transparent hover:border-zinc-800 focus:border-zinc-600 outline-none rounded px-1.5 py-0.5 text-right w-full text-[12px] font-mono text-zinc-200"
      placeholder={col.type === "currency" ? "₹0" : col.type === "number" ? "0" : "—"}
    />
  );
}

function MetaAdsBlockSettings({ cfg, campaigns, onSave, onClose }) {
  const [title, setTitle] = useState(cfg.title || "Active campaigns");
  const [limit, setLimit] = useState(cfg.row_limit || 25);
  const [selected, setSelected] = useState(cfg.selected_campaign_ids || []);
  const [metrics, setMetrics] = useState(cfg.visible_metrics && cfg.visible_metrics.length ? cfg.visible_metrics : ["daily_budget", "spend", "impressions", "clicks", "ctr", "cpm", "cpc"]);
  const [extras, setExtras] = useState(cfg.extra_columns || []);

  const toggleMetric = (k) => setMetrics(metrics.includes(k) ? metrics.filter((x) => x !== k) : [...metrics, k]);
  const toggleCampaign = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="tt-card p-4 mb-4 space-y-4 anim-fade-up" data-testid="meta-ads-settings">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <div className="t-eyebrow mb-1.5">Block title</div>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" data-testid="meta-ads-cfg-title" />
        </div>
        <div>
          <div className="t-eyebrow mb-1.5">Row limit</div>
          <Input type="number" min="1" max="500" value={limit} onChange={(e) => setLimit(parseInt(e.target.value) || 25)} className="bg-[#0a0a0a] border-zinc-800 h-8 text-[12px]" data-testid="meta-ads-cfg-limit" />
        </div>
      </div>

      <div>
        <div className="t-eyebrow mb-1.5">Visible metrics</div>
        <div className="flex flex-wrap gap-1.5">
          {ALL_METRICS.map((m) => (
            <button key={m.key} type="button" onClick={() => toggleMetric(m.key)}
              data-testid={`meta-ads-metric-${m.key}`}
              className={`px-2.5 h-7 rounded-full text-[11px] border ${metrics.includes(m.key) ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="t-eyebrow mb-1.5">Pick specific campaigns (leave empty for all active)</div>
        <div className="max-h-40 overflow-y-auto border border-zinc-800 rounded-md p-2 bg-[#0a0a0a] space-y-1" data-testid="meta-ads-campaign-picker">
          {campaigns.length === 0 ? (
            <div className="text-zinc-600 text-[11px]">No campaigns loaded yet.</div>
          ) : campaigns.map((c) => {
            const on = selected.includes(c.campaign_id);
            return (
              <button type="button" key={c.campaign_id} onClick={() => toggleCampaign(c.campaign_id)}
                className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded text-[12px] ${on ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900"}`}>
                <span className="w-3.5 h-3.5 rounded border border-zinc-700 flex items-center justify-center">{on && <Check className="w-2.5 h-2.5 text-white" />}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-[9px] font-mono text-zinc-600">{c.effective_status}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="t-eyebrow mb-1.5 flex items-center justify-between">
          <span>Custom columns (manual data per campaign)</span>
          <Button size="sm" variant="ghost" onClick={() => setExtras([...extras, { id: crypto.randomUUID(), name: "Notes", type: "text" }])} className="h-6 text-[11px]"><Plus className="w-3 h-3 mr-1" /> add</Button>
        </div>
        <ul className="space-y-1.5">
          {extras.map((ec, i) => (
            <li key={ec.id} className="flex items-center gap-2">
              <Input value={ec.name} onChange={(e) => setExtras(extras.map((x, j) => i === j ? { ...x, name: e.target.value } : x))}
                className="bg-[#0a0a0a] border-zinc-800 h-7 text-[12px] flex-1" />
              <select value={ec.type} onChange={(e) => setExtras(extras.map((x, j) => i === j ? { ...x, type: e.target.value } : x))}
                className="bg-[#0a0a0a] border border-zinc-800 rounded h-7 text-[11px] px-2">
                <option value="text">text</option>
                <option value="number">number</option>
                <option value="currency">currency ₹</option>
              </select>
              <Button size="sm" variant="ghost" onClick={() => setExtras(extras.filter((_, j) => j !== i))} className="h-7 w-7 p-0"><X className="w-3 h-3 text-[#EF4444]" /></Button>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onClose} className="h-8 text-[11px]">Cancel</Button>
        <Button size="sm" onClick={() => onSave({ title, row_limit: limit, selected_campaign_ids: selected, visible_metrics: metrics, extra_columns: extras })}
          className="bg-white text-black hover:bg-zinc-200 h-8 text-[11px]" data-testid="meta-ads-cfg-save">Save</Button>
      </div>
    </div>
  );
}
